import { Dispatch, useCallback, useEffect, useState } from 'react';

import { useSWR } from '../useSWR';

const getStorageValue = <T>(key: string, defaultValue: T): T => {
  const saved = localStorage.getItem(key);
  const initial = saved ? JSON.parse(saved) : defaultValue;
  return initial || defaultValue;
};

const setStorageValue = <T>(key: string, value: T): void => {
  return localStorage.setItem(key, JSON.stringify(value));
};

export const useLocalStorage = <T>(
  key: string,
  defaultValue: T,
): [T, Dispatch<T>] => {
  const [value, setValue] = useState(() => {
    return getStorageValue(key, defaultValue);
  });

  useEffect(() => {
    setStorageValue(key, value);
  }, [key, value]);

  return [value, setValue];
};

type UseSyncedLocalStorageOutput<T> = {
  value: T;
  setValue: Dispatch<T>;
  refresh: () => void;
  isLoading: boolean;
};

export const useSyncedLocalStorage = <T>(
  key: string,
  defaultValue: T,
): UseSyncedLocalStorageOutput<T> => {
  const swrKey = ['local-storage', key];
  const swrConfig = {
    revalidateOnFocus: false,
  };

  const { data, mutate, isLoading } = useSWR(
    swrKey,
    () => {
      return getStorageValue<T>(key, defaultValue);
    },
    swrConfig,
  );

  const setValue = useCallback(
    (value: T) => {
      setStorageValue(key, value);
      mutate(value);
    },
    [mutate, key],
  );

  const refresh = useCallback(() => {
    mutate();
  }, [mutate]);

  const value = data ?? defaultValue;
  return { value, setValue, refresh, isLoading };
};
